import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getCollection, getSiteLabel, requestJson } from '../lib/api';
import { useMenuPage } from '../hooks/useMenuPage';

function shortId(value) {
  return String(value || '').slice(-7);
}

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('fr-FR');
}

function formatFileSize(value) {
  return `${Math.round((value || 0) / 1024)} Ko`;
}

function getWarrantyLabel(value) {
  const labels = {
    supplier_warranty: 'Garantie fournisseur',
    customer_quote: 'Commande client à facturer',
    out_of_warranty: 'Hors garantie',
    unknown: 'À confirmer',
  };
  return labels[value] || value || '—';
}

const orderStatuses = new Set(['new', 'store_answer', 'new_comment']);
const followupStatuses = new Set(['supplier_followup']);

export default function LogisticsOrdersPage() {
  const menuPage = useMenuPage('/sav-commandes');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState('');
  const [previewLoadingId, setPreviewLoadingId] = useState('');
  const [preview, setPreview] = useState(null);
  const [supplierFilter, setSupplierFilter] = useState('');
  const [selectedOrderIds, setSelectedOrderIds] = useState({ type: 'include', ids: new Set() });
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const payload = await requestJson('/sav-requests?limit=500');
      setRows(getCollection(payload).filter((row) => orderStatuses.has(row.status) || followupStatuses.has(row.status)));
    } catch (err) {
      setError(err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const orderRows = useMemo(() => rows.filter((row) => orderStatuses.has(row.status)), [rows]);
  const followupRows = useMemo(() => rows.filter((row) => followupStatuses.has(row.status)), [rows]);

  const supplierOptions = useMemo(() => {
    const optionsFor = (items) =>
      Array.from(new Set(items.map((row) => String(row.supplier || '').trim()).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b, 'fr')
      );
    return {
      order: optionsFor(orderRows),
      followup: optionsFor(followupRows),
    };
  }, [followupRows, orderRows]);

  const selectedSupplier = supplierFilter || '';
  const [followupSupplierFilter, setFollowupSupplierFilter] = useState('');
  const selectedFollowupSupplier = followupSupplierFilter || '';

  const filteredOrderRows = useMemo(
    () => orderRows.filter((row) => !selectedSupplier || String(row.supplier || '').trim() === selectedSupplier),
    [orderRows, selectedSupplier]
  );

  const filteredFollowupRows = useMemo(
    () => followupRows.filter((row) => !selectedFollowupSupplier || String(row.supplier || '').trim() === selectedFollowupSupplier),
    [followupRows, selectedFollowupSupplier]
  );

  const selectedOrderRows = useMemo(() => {
    const selectedIds = selectedOrderIds?.ids instanceof Set
      ? selectedOrderIds.ids
      : new Set(selectedOrderIds?.ids || []);
    return filteredOrderRows.filter((row) => selectedIds.has(row._id));
  }, [filteredOrderRows, selectedOrderIds]);

  const handleOpenPreview = async (mode = 'order') => {
    const supplierToPreview = mode === 'followup' ? selectedFollowupSupplier : selectedSupplier;
    if (!supplierToPreview) {
      setError('Sélectionne un fournisseur avant de préparer l’envoi.');
      return;
    }
    try {
      setPreviewLoadingId(`${mode}:${supplierToPreview}`);
      setError('');
      setMessage('');
      const requestIds = mode === 'order' ? selectedOrderRows.map((row) => row._id) : [];
      if (mode === 'order' && !requestIds.length) {
        setError('Coche au moins une demande à inclure dans l’envoi fournisseur.');
        return;
      }
      const query = new URLSearchParams({ supplier: supplierToPreview, mode });
      if (requestIds.length) query.set('requestIds', requestIds.join(','));
      const payload = await requestJson(`/sav-requests/supplier-email-preview?${query.toString()}`);
      setPreview(payload);
    } catch (err) {
      setError(err.message);
    } finally {
      setPreviewLoadingId('');
    }
  };

  const handleSend = async () => {
    const supplierToSend = preview?.supplier || selectedSupplier;
    const modeToSend = preview?.mode || 'order';
    if (!preview?.items?.length || !supplierToSend) return;
    try {
      setSendingId(`${modeToSend}:${supplierToSend}`);
      setError('');
      setMessage('');
      const result = await requestJson('/sav-requests/send-supplier-email', {
        method: 'POST',
        body: JSON.stringify({
          supplier: supplierToSend,
          mode: modeToSend,
          requestIds: modeToSend === 'order' ? (preview.items || []).map((item) => item._id) : undefined,
        }),
      });
      setMessage(
        result.mail?.skipped
          ? `${result.count || preview.items.length} demande(s) ${modeToSend === 'followup' ? 'relancée(s)' : 'préparée(s)'} pour ${result.mail.to}, mais SMTP inactif.`
          : `${result.count || preview.items.length} demande(s) ${modeToSend === 'followup' ? 'relancée(s)' : 'envoyée(s)'} à ${result.mail?.to || supplierToSend}.`
      );
      setPreview(null);
      setSelectedOrderIds({ type: 'include', ids: new Set() });
      await loadRows();
      window.dispatchEvent(new Event('opticdev:logistics-updated'));
    } catch (err) {
      setError(err.message);
    } finally {
      setSendingId('');
    }
  };

  const gridRows = useMemo(
    () =>
      filteredOrderRows.map((row) => ({
        ...row,
        id: row._id,
        requestNumber: row.requestNumber || shortId(row._id),
        siteLabel: row.siteId?.code || getSiteLabel(row.siteId),
        warrantyLabel: getWarrantyLabel(row.warrantyStatus),
        createdDate: formatDate(row.createdAt),
        attachmentCount: row.attachments?.length || 0,
      })),
    [filteredOrderRows]
  );

  const followupGridRows = useMemo(
    () =>
      filteredFollowupRows.map((row) => ({
        ...row,
        id: row._id,
        requestNumber: row.requestNumber || shortId(row._id),
        siteLabel: row.siteId?.code || getSiteLabel(row.siteId),
        warrantyLabel: getWarrantyLabel(row.warrantyStatus),
        createdDate: formatDate(row.createdAt),
        orderedDate: formatDate(row.supplierOrderEmail?.sentAt),
        attachmentCount: row.attachments?.length || 0,
      })),
    [filteredFollowupRows]
  );

  const columns = useMemo(
    () => [
      { field: 'requestNumber', headerName: 'N° SAV', minWidth: 155, flex: 0.7 },
      { field: 'siteLabel', headerName: 'Magasin', minWidth: 105, flex: 0.45 },
      { field: 'supplier', headerName: 'Fournisseur', minWidth: 140, flex: 0.75 },
      {
        field: 'warrantyLabel',
        headerName: 'Garantie',
        minWidth: 190,
        flex: 0.9,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value || '—'}
            color={params.value === 'Commande client à facturer' ? 'warning' : 'default'}
            variant="outlined"
            sx={{ fontWeight: 800 }}
          />
        ),
      },
      { field: 'brand', headerName: 'Marque', minWidth: 130, flex: 0.7 },
      { field: 'model', headerName: 'Modèle', minWidth: 170, flex: 1 },
      { field: 'replacementPart', headerName: 'Pièce', minWidth: 170, flex: 0.9 },
      { field: 'customerName', headerName: 'Client', minWidth: 170, flex: 0.9 },
      {
        field: 'attachmentCount',
        headerName: 'Pièces',
        minWidth: 90,
        flex: 0.4,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => <Chip size="small" label={params.value || 0} color={params.value ? 'primary' : 'default'} />,
      },
      { field: 'createdDate', headerName: 'Création', minWidth: 110, flex: 0.55 },
      {
        field: 'actions',
        headerName: 'Action',
        width: 80,
        sortable: false,
        filterable: false,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ width: '100%' }}>
            <Tooltip title="Voir dans les demandes">
              <IconButton size="small" onClick={() => window.location.assign(`/sav?q=${params.row.shortReference}`)}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    []
  );

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="overline" color="primary" sx={{ fontWeight: 900 }}>
            {menuPage.sectionTitle}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>
            Logistique - Commandes
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadRows}>
          Rafraîchir
        </Button>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="success">{message}</Alert>}

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
            <LocalShippingRoundedIcon color="primary" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                Demandes à commander fournisseur
              </Typography>
              <Typography color="text.secondary">
                {orderRows.length} demande{orderRows.length > 1 ? 's' : ''} prête{orderRows.length > 1 ? 's' : ''} à être commandée.
              </Typography>
            </Box>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 2 }}>
            <TextField
              select
              size="small"
              label="Fournisseur"
              value={supplierFilter}
              onChange={(event) => setSupplierFilter(event.target.value)}
              sx={{ minWidth: { xs: '100%', md: 260 } }}
            >
              <MenuItem value="">Tous les fournisseurs</MenuItem>
              {supplierOptions.order.map((supplier) => (
                <MenuItem key={supplier} value={supplier}>
                  {supplier}
                </MenuItem>
              ))}
            </TextField>
            <Chip
              label={`${selectedOrderRows.length} sélectionnée${selectedOrderRows.length > 1 ? 's' : ''} sur ${gridRows.length}`}
              color={selectedSupplier ? 'primary' : 'default'}
              variant={selectedSupplier ? 'filled' : 'outlined'}
              sx={{ fontWeight: 850 }}
            />
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              startIcon={<SendRoundedIcon />}
              onClick={() => handleOpenPreview('order')}
              disabled={!selectedSupplier || !selectedOrderRows.length || Boolean(previewLoadingId)}
              sx={{ fontWeight: 900 }}
            >
              {previewLoadingId.startsWith('order:') ? 'Préparation...' : 'Prévisualiser l’envoi fournisseur'}
            </Button>
          </Stack>

          {!selectedSupplier && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Sélectionne un fournisseur, puis coche les demandes à inclure dans le mail groupé.
            </Alert>
          )}

          {loading ? (
            <Stack alignItems="center" sx={{ py: 8 }}>
              <CircularProgress />
            </Stack>
          ) : (
            <DataGrid
              rows={gridRows}
              columns={columns}
              checkboxSelection
              rowSelectionModel={selectedOrderIds}
              onRowSelectionModelChange={setSelectedOrderIds}
              isRowSelectable={() => Boolean(selectedSupplier)}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              pageSizeOptions={[10, 25, 50, 100]}
              disableRowSelectionOnClick
              autoHeight
              sx={{ border: 0 }}
            />
          )}
        </CardContent>
      </Card>

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
            <CampaignRoundedIcon color="warning" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                Relances fournisseur
              </Typography>
              <Typography color="text.secondary">
                {followupRows.length} demande{followupRows.length > 1 ? 's' : ''} déjà commandée{followupRows.length > 1 ? 's' : ''} en attente de retour fournisseur.
              </Typography>
            </Box>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 2 }}>
            <TextField
              select
              size="small"
              label="Fournisseur"
              value={followupSupplierFilter}
              onChange={(event) => setFollowupSupplierFilter(event.target.value)}
              sx={{ minWidth: { xs: '100%', md: 260 } }}
            >
              <MenuItem value="">Tous les fournisseurs</MenuItem>
              {supplierOptions.followup.map((supplier) => (
                <MenuItem key={supplier} value={supplier}>
                  {supplier}
                </MenuItem>
              ))}
            </TextField>
            <Chip
              label={`${followupGridRows.length} demande${followupGridRows.length > 1 ? 's' : ''} affichée${followupGridRows.length > 1 ? 's' : ''}`}
              color={selectedFollowupSupplier ? 'warning' : 'default'}
              variant={selectedFollowupSupplier ? 'filled' : 'outlined'}
              sx={{ fontWeight: 850 }}
            />
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              color="warning"
              startIcon={<CampaignRoundedIcon />}
              onClick={() => handleOpenPreview('followup')}
              disabled={!selectedFollowupSupplier || !followupGridRows.length || Boolean(previewLoadingId)}
              sx={{ fontWeight: 900 }}
            >
              {previewLoadingId.startsWith('followup:') ? 'Préparation...' : 'Prévisualiser la relance'}
            </Button>
          </Stack>

          {!selectedFollowupSupplier && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Filtre par fournisseur pour préparer un mail de relance groupé.
            </Alert>
          )}

          {loading ? (
            <Stack alignItems="center" sx={{ py: 8 }}>
              <CircularProgress />
            </Stack>
          ) : (
            <DataGrid
              rows={followupGridRows}
              columns={columns}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              pageSizeOptions={[10, 25, 50, 100]}
              disableRowSelectionOnClick
              autoHeight
              sx={{ border: 0 }}
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(preview)} onClose={() => !sendingId && setPreview(null)} fullWidth maxWidth="md">
        <DialogTitle>{preview?.mode === 'followup' ? 'Prévisualisation de la relance fournisseur' : 'Prévisualisation du mail fournisseur'}</DialogTitle>
        <DialogContent>
          {preview && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="info">
                {preview.mode === 'followup'
                  ? 'Vérifie le contenu avant l’envoi. Les dossiers passeront ensuite en statut Relance fournisseur.'
                  : 'Vérifie le contenu avant l’envoi. Les dossiers passeront ensuite en attente de confirmation fournisseur, jusqu’à la saisie du numéro de commande.'}
              </Alert>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '140px 1fr' }, gap: 1 }}>
                <Typography color="text.secondary">À</Typography>
                <Typography sx={{ fontWeight: 850 }}>{preview.mail.to}</Typography>
                <Typography color="text.secondary">Copie</Typography>
                <Typography>{preview.mail.cc || '—'}</Typography>
                <Typography color="text.secondary">Compte</Typography>
                <Typography>{preview.mail.account || '—'}</Typography>
                <Typography color="text.secondary">Sujet</Typography>
                <Typography sx={{ fontWeight: 850 }}>{preview.mail.subject}</Typography>
              </Box>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  p: 2,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                  Aperçu du message
                </Typography>
                <Box
                  sx={{
                    color: 'text.primary',
                    '& p': { my: 0.8 },
                    '& br': { display: 'block', content: '""', mt: 0.8 },
                    overflowWrap: 'anywhere',
                  }}
                  dangerouslySetInnerHTML={{ __html: preview.mail.html || '' }}
                />
              </Box>
              <TableContainer
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  maxHeight: 360,
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Compte</TableCell>
                      <TableCell>Magasin</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Marque</TableCell>
                      <TableCell>Modèle</TableCell>
                      <TableCell>Pièce</TableCell>
                      <TableCell>Garantie</TableCell>
                      <TableCell align="center">PJ</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(preview.mail.rows || []).map((row) => (
                      <TableRow key={row.id}>
                        <TableCell sx={{ fontWeight: 900 }}>{row.id}</TableCell>
                        <TableCell>{row.account || '—'}</TableCell>
                        <TableCell>{row.store}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.requestType || 'SAV fournisseur'}
                            color={row.requestType === 'Commande client à facturer' ? 'warning' : 'primary'}
                            variant={row.requestType === 'Commande client à facturer' ? 'filled' : 'outlined'}
                            sx={{ fontWeight: 850 }}
                          />
                        </TableCell>
                        <TableCell>{row.customer}</TableCell>
                        <TableCell>{row.brand}</TableCell>
                        <TableCell>{row.model}</TableCell>
                        <TableCell>{row.part}</TableCell>
                        <TableCell>{row.warranty}</TableCell>
                        <TableCell align="center">
                          <Chip size="small" label={row.attachmentCount || 0} color={row.attachmentCount ? 'primary' : 'default'} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                  Pièces jointes
                </Typography>
                {preview.mail.attachments?.length ? (
                  preview.mail.attachments.map((attachment) => (
                    <Stack key={`${attachment.requestReference || 'generated'}-${attachment.id || attachment.name}`} direction="row" spacing={1} alignItems="center">
                      <AttachFileIcon fontSize="small" color={attachment.exists ? 'primary' : 'error'} />
                      <Chip
                        size="small"
                        label={attachment.generated ? 'Excel fournisseur' : attachment.requestReference}
                        color={attachment.generated ? 'success' : 'default'}
                        variant={attachment.generated ? 'filled' : 'outlined'}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>
                        {attachment.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(attachment.size)}
                        {attachment.generated && attachment.rowCount ? ` · ${attachment.rowCount} ligne${attachment.rowCount > 1 ? 's' : ''}` : ''}
                      </Typography>
                      {!attachment.exists && <Chip size="small" color="error" label="fichier introuvable" />}
                    </Stack>
                  ))
                ) : preview.mode === 'followup' ? (
                  <Alert severity="info">La relance est envoyée sans pièce jointe.</Alert>
                ) : (
                  <Alert severity="warning">Aucune pièce jointe attachée au mail.</Alert>
                )}
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview(null)} disabled={Boolean(sendingId)}>
            Annuler
          </Button>
          <Button
            variant="contained"
            startIcon={<SendRoundedIcon />}
            onClick={handleSend}
            disabled={Boolean(sendingId)}
          >
            {sendingId ? 'Envoi...' : preview?.mode === 'followup' ? 'Confirmer la relance' : 'Confirmer l’envoi'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
